import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setAccessToken, setRefreshToken } from "../api/client";
import { User } from "../types/user";
import { AuthState } from "../types/authState";

const AuthCtx = createContext<AuthState | undefined>(undefined);

export const AuthProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.post("/api/auth/refresh", {});
                setAccessToken(data.accessToken);
                if (data.refreshToken) setRefreshToken(data.refreshToken);
                const me = await api.get<User>("/api/users/me");
                setUser(me.data);
            } catch {
                setUser(null);
                setAccessToken(null);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get<User>("/api/users/me");
            console.log("Fetched user data:", data);
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const login = async (email: string, password: string) => {
        console.log("Attempting login with email:", email);
        console.log("Password provided:", password ? "Yes" : "No");
        const { data } = await api.post("/api/auth/login", { email, password });
        console.log("Login response:", data);
        setAccessToken(data.accessToken);
        if (data.refreshToken) setRefreshToken(data.refreshToken);
        const me = await api.get<User>("/api/users/me");
        setUser(me.data);
    };

    const register = useCallback(async (email: string, password: string) => {
        const { data } = await api.post<{ accessToken: string }>("/api/auth/register", { email, password});
        setAccessToken(data.accessToken);
        await fetchMe();
        //await api.post("/api/auth/register", { email, password, displayName });
    }, [fetchMe]);

    const logout = async () => {
        try {
            const refreshToken = localStorage.getItem("refreshToken");
            await api.post("/api/auth/logout", { refreshToken });
        } finally {
            setUser(null);
            setAccessToken(null);
            setRefreshToken(null);
        }
    };

    const verifyUserEmail = async (email: string, code: string) => {
        const { data } = await api.post("/api/auth/verifyEmailCode", { email, code });
        setAccessToken(data.accessToken);
        if (data.refreshToken) setRefreshToken(data.refreshToken);
        const me = await api.get<User>("/api/users/me");
        setUser(me.data);
    };

    const value = useMemo(() => ({ user, loading, login, register, logout, fetchMe, verifyUserEmail }), [user, register, loading, fetchMe]);
    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};